import { useEffect, useState } from 'react';

/**
 * Light/dark theme switching — sets the `data-theme` attribute on <html>,
 * persists to localStorage. Matches DreamsPOS template's mechanism but with
 * localStorage instead of sessionStorage so the choice survives reboots.
 */
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'codedpos.theme';

function readInitial(): Theme {
  if (typeof window === 'undefined') return 'light';
  const saved = localStorage.getItem(STORAGE_KEY) as Theme | null;
  if (saved === 'light' || saved === 'dark') return saved;
  // Default to LIGHT regardless of system preference — explicit owner directive.
  // Cashiers should never get dark by accident from device settings.
  return 'light';
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(readInitial);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);
    // Also flip the meta theme-color so iOS standalone PWA chrome matches.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', theme === 'dark' ? '#01060B' : '#0D76E1');
    }
  }, [theme]);

  const setTheme = (t: Theme) => setThemeState(t);
  const toggleTheme = () => setThemeState((t) => (t === 'light' ? 'dark' : 'light'));

  return { theme, setTheme, toggleTheme };
}

/** Sync the data-theme attribute on initial load before React mounts. */
export function applyInitialTheme() {
  if (typeof window === 'undefined') return;
  document.documentElement.setAttribute('data-theme', readInitial());
}
